/**
 * Shibumi API Service
 * Handles communication with the Shibumi backend API
 */

/* global console, fetch, TextDecoder, FormData */

import axios from "axios";
import { API_CONFIG, ERROR_MESSAGES } from "../config/api.js";
import { isCredentialFailure, isTermsAcceptanceRequired } from "./authFailure.js";
import { reportApiFailure } from "./sentryReporting.js";
import {
  clearLegacyApiKey,
  clearWebSession,
  getAuthTransportSnapshot,
  getRequestAuthHeaders,
  isCurrentAuthTransport,
} from "../auth/session.js";

// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: true,
});

export function notifyAuthFailure(authSnapshot) {
  const snapshot = authSnapshot || getAuthTransportSnapshot();
  if (!isCurrentAuthTransport(snapshot)) return false;
  const credentialMethod = snapshot.credentialMethod;
  clearWebSession();
  clearLegacyApiKey();
  window.dispatchEvent(
    new CustomEvent("auth-session-invalid", {
      detail: { credentialMethod },
    })
  );
  return true;
}

export const TERMS_REQUIRED_MESSAGE = "Accept the Terms of Use to continue.";

/**
 * The server refused a call because this session has not accepted the current
 * terms (they changed while the pane was open). Unlike notifyAuthFailure this
 * keeps the session: the pane shows the terms over the chat and carries on
 * where it was once they are accepted.
 */
export function notifyTermsAcceptanceRequired(authSnapshot) {
  const snapshot = authSnapshot || getAuthTransportSnapshot();
  if (!isCurrentAuthTransport(snapshot)) return false;
  window.dispatchEvent(new CustomEvent("terms-acceptance-required"));
  return true;
}

// Request interceptor: attach API key and handle FormData.
apiClient.interceptors.request.use(
  (config) => {
    const authSnapshot = getAuthTransportSnapshot();
    config.novatimeAuthSnapshot = authSnapshot;
    // A legacy request must not also carry a stale HttpOnly web cookie.
    config.withCredentials = authSnapshot.credentialMethod !== "legacy";
    const authHeaders = getRequestAuthHeaders(config.method || "GET");
    for (const [name, value] of Object.entries(authHeaders)) {
      if (config.headers && typeof config.headers.set === "function") {
        config.headers.set(name, value);
      } else if (config.headers) {
        config.headers[name] = value;
      }
    }

    const isFormDataPayload =
      typeof FormData !== "undefined" && config?.data instanceof FormData;

    // Let browser/XHR set multipart boundaries for FormData uploads.
    // Keeping JSON content-type here causes FastAPI to miss the `file` field (422).
    if (isFormDataPayload) {
      if (config.headers && typeof config.headers.set === "function") {
        config.headers.set("Content-Type", undefined);
        config.headers.set("Accept", "application/json");
      } else if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
        config.headers.Accept = "application/json";
      }
    }

    return config;
  },
  (error) => {
    console.error("API request setup failed:", error?.message || "unknown error");
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // GID-83: 5xx, network drops and timeouts go to Sentry (4xx are expected
    // flows). Reads the raw axios error, before it is reshaped below.
    reportApiFailure(error);
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`API response failed with HTTP ${status}`);

      // Map status codes to error messages
      let message = ERROR_MESSAGES.unknown;
      let errorType = "server_error";

      if (status === 400) message = ERROR_MESSAGES.validation;
      else if (isTermsAcceptanceRequired(status, data)) {
        message = TERMS_REQUIRED_MESSAGE;
        errorType = "terms_required";
        notifyTermsAcceptanceRequired(
          error.config?.novatimeAuthSnapshot || error.response?.config?.novatimeAuthSnapshot
        );
      }
      else if (status === 401 || status === 403) {
        message = status === 401 ? ERROR_MESSAGES.unauthorized : ERROR_MESSAGES.forbidden;
        if (isCredentialFailure(status, data)) {
          errorType = "auth_error";
          notifyAuthFailure(
            error.config?.novatimeAuthSnapshot ||
              error.response?.config?.novatimeAuthSnapshot
          );
        } else {
          errorType = "authorization_error";
        }
      }
      else if (status === 404) message = ERROR_MESSAGES.notFound;
      else if (status === 429) {
        message = data?.detail || data?.error || ERROR_MESSAGES.rateLimit;
        errorType = "rate_limit";
      }
      else if (status === 503) {
        message = ERROR_MESSAGES.heavyLoad;
        errorType = "heavy_load";
      }
      else if (status >= 500) message = ERROR_MESSAGES.server;

      // Return structured error
      const responseMessage = status === 429 ? message : (data?.error || data?.detail || message);
      return Promise.reject({
        type: errorType,
        status,
        message: responseMessage,
        retryAfterSeconds: data?.retry_after_seconds || null,
        details: data,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error("API network request failed:", error?.message || "no response");
      return Promise.reject({
        type: "network_error",
        message: ERROR_MESSAGES.network,
        details: { code: error.code || "NETWORK_ERROR" },
      });
    } else if (error.code === "ECONNABORTED") {
      // Request timeout
      console.error("Request Timeout:", error.message);
      return Promise.reject({
        type: "timeout_error",
        message: ERROR_MESSAGES.timeout,
        details: { code: error.code || "ECONNABORTED" },
      });
    } else {
      // Something else happened
      console.error("Request Setup Error:", error.message);
      return Promise.reject({
        type: "request_error",
        message: error.message || ERROR_MESSAGES.unknown,
        details: { code: error.code || "REQUEST_ERROR" },
      });
    }
  }
);

/**
 * The attachment endpoints answer errors as `{error, code}`, and the upload
 * queue decides whether to retry from the HTTP status, so both must survive.
 * The response interceptor already sets them for HTTP errors (preferring
 * `error`, then `detail`), except that it puts a 429's `detail` first and that
 * an aborted request comes out looking like a network failure -- which would
 * read as "retry me". Normalise both here.
 */
function toAttachmentError(error, defaultMessage, signal = null) {
  const body = error?.details && typeof error.details === "object" ? error.details : null;
  const status = Number(error?.status ?? error?.response?.status) || undefined;
  // FastAPI's own 422s carry `detail` as a list of objects, not a sentence.
  const asText = (value) => (typeof value === "string" && value.trim() ? value : null);
  const bodyMessage = status ? asText(body?.error) || asText(body?.detail) : null;
  return {
    ...(error && typeof error === "object" ? error : {}),
    type: error?.type || "unknown_error",
    status,
    message: bodyMessage || asText(error?.message) || defaultMessage,
    aborted: Boolean(signal?.aborted),
  };
}

/**
 * Chat API Service
 */
class ChatAPI {
  /**
   * Send a message to the chat API
   * @param {Object} messageData - Message data
   * @param {string} messageData.message - The message text
   * @param {string} [messageData.session_id] - Optional session ID
   * @param {string} [messageData.user_id] - Optional user ID
   * @param {Object} [messageData.project_context] - Optional project context
   * @param {string} [messageData.project_change_notification] - Optional project change notification
   * @param {boolean} [messageData.stream] - Whether to use streaming
   * @returns {Promise<Object>} API response
   */
  async sendMessage(messageData, signal) {
    try {
      const response = await apiClient.post("/chat/message", messageData, signal ? { signal } : undefined);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to send message");
    }
  }

  /**
   * A rating of a reply, or a problem report (GID-83).
   * @param {Object} feedback - {kind, rating?, reasons?, comment?, session_id?, message_id?, langfuse_trace_id?}
   * @returns {Promise<Object>} {status: "recorded" | "rate_limited"}
   */
  async sendFeedback(feedback) {
    try {
      const response = await apiClient.post("/chat/feedback", feedback);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to send feedback");
    }
  }

  /**
   * Attach a file to a chat session (T12 endpoint).
   * Uploads the file as multipart/form-data; the backend extracts text,
   * optionally summarises, and returns an ATTACHED_FILE message record.
   * @param {string} sessionId - Active chat session id
   * @param {File|Blob} file - File from <input type="file">
   * @returns {Promise<Object>} { message_id, filename, size, was_summarised, ... }
   */
  async attachFile(sessionId, file, signal = null) {
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const url = `/chat/${encodeURIComponent(sessionId)}/attach`;
      const response = await apiClient.post(url, form, signal ? { signal } : undefined);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to attach file");
    }
  }

  /**
   * GID-220: start attaching a file without waiting for the server to read it.
   * The server stores the bytes and answers at once; extraction, OCR and
   * summarising carry on in the background (poll getAttachmentStatus).
   * Re-posting identical bytes to the same session is deduped server-side, so
   * a retry after a dropped connection cannot attach the file twice.
   * @returns {Promise<Object>} 202 `{file_id, filename, size, status: "processing", stage}`
   *   or, for bytes already attached and read, 200 `{file_id, status: "ready", message_id, ...}`
   * @throws {{status?: number, message: string, aborted?: boolean}} message is user-readable
   */
  async attachFileAsync(sessionId, file, signal = null) {
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const url = `/chat/${encodeURIComponent(sessionId)}/attach?async=1`;
      const response = await apiClient.post(url, form, signal ? { signal } : undefined);
      return response.data;
    } catch (error) {
      throw toAttachmentError(error, "Failed to attach file", signal);
    }
  }

  /**
   * GID-220: where the server is with a file posted by attachFileAsync.
   * @returns {Promise<Object>} `{file_id, filename, size, status, stage, message_id?, error?, ...}`
   */
  async getAttachmentStatus(sessionId, fileId, signal = null) {
    try {
      const url = `/chat/${encodeURIComponent(sessionId)}/attach/${encodeURIComponent(fileId)}/status`;
      const response = await apiClient.get(url, signal ? { signal } : undefined);
      return response.data;
    } catch (error) {
      throw toAttachmentError(error, "Failed to check the attached file", signal);
    }
  }

  /**
   * Remove a previously-attached file from a chat session. Deletes the
   * ATTACHED_FILE message so its content stops entering the LLM context
   * on later turns (attaching then deleting no longer leaves it live).
   * Accepts a message_id, or (GID-220) a file_id, which also cancels a file
   * the server is still reading.
   * @param {string} sessionId - Active chat session id
   * @param {string} messageId - message_id (or file_id) of the attached file
   * @returns {Promise<Object>} { deleted, message_id }
   */
  async deleteAttachedFile(sessionId, messageId) {
    try {
      const url = `/chat/${encodeURIComponent(sessionId)}/attach/${encodeURIComponent(messageId)}`;
      const response = await apiClient.delete(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to remove attached file");
    }
  }

  /**
   * Send a message with streaming response
   * Handles both SSE (Server-Sent Events) and NDJSON (JSON Lines) formats
   * Uses async generator pattern for cleaner code
   * Includes retry mechanism and timeout handling for production
   * @param {Object} messageData - Message data
   * @param {string} [messageData.message] - The message text
   * @param {string} [messageData.session_id] - Optional session ID
   * @param {string} [messageData.user_id] - Optional user ID
   * @param {Object} [messageData.project_context] - Optional project context
   * @param {Function} onChunk - Callback for each chunk: (chunk: string) => void
   * @param {Function} onComplete - Callback when complete: (data: Object) => void
   * @param {Function} [onEvent] - Callback for stream events: (data: Object) => void
   * @param {Function} onError - Callback for errors: (error: Object) => void
   * @param {AbortSignal} [signal] - Optional external abort signal
   * @returns {Promise<void>}
   */
  async sendMessageStream(messageData, onChunk, onComplete, onError, onEvent, signal) {
    let lastError = null;
    let retryCount = 0;
    const maxRetries = 2; // Retry up to 2 times

    while (retryCount <= maxRetries) {
      let requestAccepted = false;
      try {
        // Create AbortController for timeout handling
        const controller = new AbortController();
        const abortStream = () => controller.abort();
        if (signal) {
          if (signal.aborted) {
            controller.abort();
          } else {
            signal.addEventListener("abort", abortStream, { once: true });
          }
        }
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, API_CONFIG.timeout);

        try {
          const authSnapshot = getAuthTransportSnapshot();
          const response = await fetch(
            `${API_CONFIG.baseURL}${API_CONFIG.endpoints.chat.messageStream}`,
            {
              method: "POST",
              headers: {
                ...API_CONFIG.headers,
                ...getRequestAuthHeaders("POST"),
                "Connection": "keep-alive",
                "Cache-Control": "no-cache",
              },
              body: JSON.stringify({ ...messageData, stream: true }),
              credentials: authSnapshot.credentialMethod === "legacy" ? "omit" : "include",
              signal: controller.signal, // Add abort signal for timeout
            }
          );

          // GID-16: the connect timeout is done once headers arrive, but the
          // user's Stop signal must stay wired to controller.abort() through the
          // ENTIRE body-streaming phase below. For SSE the body streams AFTER
          // fetch() resolves, so removing the listener here made Stop a no-op
          // mid-stream — the response kept arriving and rendered "later". The
          // listener is removed in the finally, after streaming ends.
          clearTimeout(timeoutId);

          if (!response.ok) {
            let errorPayload = null;
            try {
              errorPayload = await response.json();
            } catch {
              errorPayload = null;
            }

            if (isTermsAcceptanceRequired(response.status, errorPayload)) {
              notifyTermsAcceptanceRequired(authSnapshot);
              throw {
                type: "terms_required",
                status: response.status,
                message: TERMS_REQUIRED_MESSAGE,
                details: errorPayload,
              };
            }

            if (response.status === 401 || response.status === 403) {
              const credentialFailure = isCredentialFailure(response.status, errorPayload);
              if (credentialFailure) {
                notifyAuthFailure(authSnapshot);
              }
              throw {
                type: credentialFailure ? "auth_error" : "authorization_error",
                status: response.status,
                message: errorPayload?.detail || errorPayload?.error || (response.status === 401 ? ERROR_MESSAGES.unauthorized : ERROR_MESSAGES.forbidden),
                details: errorPayload || { status: response.status },
              };
            }

            if (response.status === 429) {
              const retryAfterHeader = response.headers.get("Retry-After");
              const retryAfterSeconds = Number(errorPayload?.retry_after_seconds || retryAfterHeader || 30);
              throw {
                type: "rate_limit",
                status: response.status,
                message: errorPayload?.detail || errorPayload?.error || ERROR_MESSAGES.rateLimit,
                retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : 30,
                details: errorPayload || { status: response.status },
              };
            }

            if (response.status === 503) {
              throw {
                type: "heavy_load",
                status: response.status,
                message: errorPayload?.detail || errorPayload?.error || ERROR_MESSAGES.heavyLoad,
                details: errorPayload || { status: response.status },
              };
            }

            throw new Error(`HTTP error! status: ${response.status}`);
          }

          if (!response.body) {
            throw new Error("Response body is not readable");
          }

          // The backend has accepted the request and started work on it. From
          // here a failure must not re-send the message: that started a second
          // edit run beside the first, and in production one instruction ran
          // up to seven times while the user waited (GID-219).
          requestAccepted = true;

          // Use async generator for cleaner streaming
          let hasData = false;
          for await (const data of this.streamResponse(response.body)) {
            hasData = true;
            const processed = this.processStreamData(data, onChunk, onComplete, onEvent);

            // If stream is complete, break
            if (processed === "complete") {
              return; // Success - exit function
            }
          }

          // If we got here and no data was received, it might be an incomplete stream
          if (!hasData) {
            throw new Error("No data received from stream");
          }

          // Data arrived but the stream ended without its completion marker:
          // the connection dropped mid-reply. Without this neither onComplete
          // nor onError ran, and the pane waited forever (GID-83 review). Not
          // retried: the backend accepted the request and may still finish it.
          throw {
            type: "stream_incomplete",
            message: "The connection dropped before the reply finished. Check the chat in a moment, or try again.",
          };
        } catch (fetchError) {
          clearTimeout(timeoutId);

          // Handle abort (timeout)
          if (fetchError.name === "AbortError") {
            if (signal?.aborted) {
              throw {
                type: "abort_error",
                message: "Request aborted",
              };
            }
            throw new Error("Request timeout - stream took too long");
          }

          throw fetchError;
        } finally {
          // GID-16: remove the Stop wiring only once this attempt is fully
          // done (success, abort, or error) — never before the body finishes
          // streaming. Idempotent + safe with the { once: true } listener.
          if (signal) {
            signal.removeEventListener("abort", abortStream);
          }
        }
      } catch (error) {
        lastError = error;

        // Check if error is retryable. Never once the backend has accepted
        // the request: the work is already running there.
        const isRetryable = !requestAccepted && this.isRetryableError(error);

        if (!isRetryable || retryCount >= maxRetries) {
          // Not retryable or max retries reached
          onError(this.handleError(error, "Failed to stream message"));
          return;
        }

        // Retry with exponential backoff
        retryCount++;
        const delay = API_CONFIG.retry.delay * Math.pow(API_CONFIG.retry.backoff, retryCount - 1);
        console.warn(`Streaming error, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries}):`, error);

        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // If we get here, all retries failed
    onError(this.handleError(lastError, "Failed to stream message after retries"));
  }

  /**
   * Check if an error is retryable
   * @param {Error} error - Error to check
   * @returns {boolean} True if error is retryable
   */
  isRetryableError(error) {
    if (error?.type === "abort_error") {
      return false;
    }

    if (error?.type === "rate_limit" || error?.status === 429) {
      return false;
    }

    // Network errors are retryable
    if (error.message && (
      error.message.includes("network error") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("ERR_INCOMPLETE_CHUNKED_ENCODING") ||
      error.message.includes("timeout") ||
      error.message.includes("Network error while reading stream")
    )) {
      return true;
    }

    // TypeError with network error is retryable
    if (error instanceof TypeError && error.message.includes("network")) {
      return true;
    }

    return false;
  }

  /**
   * Async generator for streaming response chunks
   * Handles buffer management for incomplete JSON strings
   * Includes error handling for incomplete chunks
   * @param {ReadableStream} body - Response body stream
   * @yields {string} Complete line from stream
   * @returns {AsyncGenerator<string, void, unknown>}
   */
  async *streamResponse(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = ""; // Buffer for incomplete JSON strings
    let lastChunkTime = Date.now();
    const keepAliveTimeout = 60000; // 60 seconds timeout for no data

    try {
      while (true) {
        // Check for keep-alive timeout
        const now = Date.now();
        if (now - lastChunkTime > keepAliveTimeout) {
          throw new Error("Stream timeout - no data received for too long");
        }

        let readResult;
        try {
          readResult = await reader.read();
        } catch (readError) {
          // Handle read errors (network issues, incomplete chunks)
          if (readError.message && readError.message.includes("network")) {
            throw new Error("Network error while reading stream");
          }
          throw readError;
        }

        const { done, value } = readResult;

        if (done) {
          // Stream ended - process any remaining buffer
          if (buffer.trim()) {
            yield buffer.trim();
          }
          break;
        }

        // Update last chunk time
        lastChunkTime = Date.now();

        // Decode chunk (handle potential encoding errors)
        let decodedChunk;
        try {
          decodedChunk = decoder.decode(value, { stream: true });
        } catch (decodeError) {
          console.warn("Error decoding chunk:", decodeError);
          // Try to continue with partial data
          continue;
        }

        buffer += decodedChunk;

        // Process complete lines
        let newlineIndex;
        while ((newlineIndex = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.trim()) {
            const trimmedLine = line.trim();
            // SSE comment, e.g. the backend's ": keepalive" during long runs.
            if (trimmedLine.startsWith(":")) {
              continue;
            }
            if (this.isCompleteJSON(trimmedLine)) {
              yield trimmedLine;
            } else {
              // Incomplete JSON - keep in buffer
              buffer = trimmedLine + "\n" + buffer;
              break;
            }
          }
        }
      }
    } catch (error) {
      // If we have partial data in buffer, try to yield it
      if (buffer.trim()) {
        try {
          yield buffer.trim();
        } catch (yieldError) {
          console.warn("Error yielding partial buffer:", yieldError);
        }
      }
      throw error;
    } finally {
      try {
        reader.releaseLock();
      } catch (releaseError) {
        console.warn("Error releasing reader lock:", releaseError);
      }
    }
  }

  /**
   * Check if a string contains complete JSON
   * Handles both SSE format (data: {...}) and NDJSON format ({...})
   * @param {string} line - Line to check
   * @returns {boolean} True if line contains complete JSON
   */
  isCompleteJSON(line) {
    try {
      let jsonString = line;

      // Handle SSE format: "data: {...}"
      if (line.startsWith("data: ")) {
        jsonString = line.slice(6);
      }

      // Skip empty lines or special markers
      if (!jsonString.trim() || jsonString.trim() === "[DONE]") {
        return true; // These are valid complete lines
      }

      // Try to parse - if successful, JSON is complete
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      // If error is about unterminated string, JSON is incomplete
      if (error.message && error.message.includes("Unterminated")) {
        return false;
      }
      // Other errors might be invalid JSON, but we'll try to parse it anyway
      return true;
    }
  }

  /**
   * Process a single data item from the stream
   * Handles both SSE format (data: {...}) and NDJSON format ({...})
   * @param {string} line - Line to process
   * @param {Function} onChunk - Callback for each chunk: (chunk: string) => void
   * @param {Function} onComplete - Callback when complete: (data: Object) => void
   * @param {Function} [onEvent] - Callback for stream events: (data: Object) => void
   * @returns {string|void} Returns "complete" if stream is done, undefined otherwise
   */
  processStreamData(line, onChunk, onComplete, onEvent) {
    try {
      let jsonString = line;

      // Handle SSE format: "data: {...}"
      if (line.startsWith("data: ")) {
        jsonString = line.slice(6); // Remove "data: " prefix
      }

      // Handle [DONE] marker - signal completion
      if (jsonString.trim() === "[DONE]") {
        onComplete({ is_complete: true, done: true });
        return "complete";
      }

      // Skip empty lines
      if (!jsonString.trim()) {
        return;
      }

      // Parse JSON
      const data = JSON.parse(jsonString);

      // Type guard: ensure data is object or string
      if (typeof data !== "object" && typeof data !== "string") {
        console.warn("Unexpected stream data type:", typeof data, data);
        return;
      }

      if (data?.metadata?.stream_event) {
        if (onEvent) {
          onEvent(data);
        }
        return;
      }

      // Handle chunk data first (even if is_complete is true)
      if (data.chunk !== undefined) {
        onChunk(data.chunk);
        // If this is also the completion, call onComplete after processing chunk
        if (data.is_complete || data.done) {
          onComplete(data);
          return "complete";
        }
        return;
      }

      // Handle completion flags (only if no chunk was processed)
      if (data.is_complete || data.done) {
        onComplete(data);
        return "complete";
      }

      // Handle direct text chunks (if server sends plain text)
      if (typeof data === "string") {
        onChunk(data);
        return;
      }

      // Handle text field
      if (data.text !== undefined) {
        onChunk(data.text);
        return;
      }
    } catch (parseError) {
      // Only log if it's not an incomplete JSON error
      if (parseError.message && !parseError.message.includes("Unterminated")) {
        console.warn("Failed to parse streaming line:", line, parseError);
      }
      // Silently ignore incomplete JSON - it will be processed in next chunk
    }
  }

  /**
   * Create a new chat session
   * @param {Object} sessionData - Session data
   * @param {string} [sessionData.title] - Session title
   * @param {string} [sessionData.user_id] - User ID
   * @param {Object} [sessionData.project_context] - Project context
   * @returns {Promise<Object>} Created session
   */
  async createSession(sessionData = {}) {
    try {
      const response = await apiClient.post("/chat/sessions", sessionData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to create session");
    }
  }

  /**
   * Get a chat session by ID
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Session data with messages
   */
  async getSession(sessionId) {
    try {
      const response = await apiClient.get(`/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to get session");
    }
  }

  /**
   * Get user's chat sessions
   * @param {string} userId - User ID
   * @param {number} [page=1] - Page number
   * @param {number} [pageSize=20] - Page size
   * @returns {Promise<Array>} List of sessions
   */
  async getUserSessions(page = 1, pageSize = 20, projectGuid = null) {
    try {
      const params = { page, page_size: pageSize };
      if (projectGuid) params.project_guid = projectGuid;
      const response = await apiClient.get("/chat/sessions", { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to get user sessions");
    }
  }

  /**
   * Update mutable fields on a session (title, project_guid, project_name,
   * pending_comparison_attachment — explicit null clears the selection).
   */
  async updateSession(sessionId, updates) {
    try {
      const response = await apiClient.patch(`/chat/sessions/${sessionId}`, updates);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to update session");
    }
  }

  /**
   * Re-key the caller's chat sessions from one project_guid to another.
   * Used on Save As: the synthetic 'sidecar-name:Project1' identity flips
   * to 'path:C:\\...\\.mpp' once the file lands on disk; without rebind,
   * the chat history appears empty under the new identity.
   *
   * @param {string} oldProjectGuid
   * @param {string} newProjectGuid
   * @param {string} [newProjectName]
   * @returns {Promise<{rebound_count: number}>}
   */
  async rebindProjectGuid(oldProjectGuid, newProjectGuid, newProjectName) {
    try {
      const body = {
        old_project_guid: oldProjectGuid,
        new_project_guid: newProjectGuid,
      };
      if (newProjectName) body.new_project_name = newProjectName;
      const response = await apiClient.post("/chat/sessions/rebind-project-guid", body);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to rebind project_guid");
    }
  }

  /**
   * Delete a chat session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteSession(sessionId) {
    try {
      const response = await apiClient.delete(`/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to delete session");
    }
  }

  /**
   * Handle API errors
   * @param {Object} error - Error object
   * @param {string} defaultMessage - Default error message
   * @returns {Object} Formatted error
   */
  handleError(error, defaultMessage) {
    if (error.type) {
      return error; // Already formatted
    }

    return {
      type: "unknown_error",
      message: defaultMessage,
      details: error,
    };
  }
}

/**
 * Health API Service
 */
class HealthAPI {
  /**
   * Check API health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      console.log("API: Making health check request to /health/");
      const response = await apiClient.get("/health/");
      console.log("API: Health response received:", response.data);
      return response.data;
    } catch (error) {
      console.error("API: Health check failed:", error);
      throw this.handleError(error, "Failed to check health");
    }
  }

  /**
   * Check if API is ready
   * @returns {Promise<Object>} Readiness status
   */
  async checkReady() {
    try {
      const response = await apiClient.get("/health/ready");
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to check readiness");
    }
  }

  /**
   * Handle health API errors
   * @param {Object} error - Error object
   * @param {string} defaultMessage - Default error message
   * @returns {Object} Formatted error
   */
  handleError(error, defaultMessage) {
    return {
      type: "health_error",
      message: defaultMessage,
      details: error,
    };
  }
}

/**
 * Files API Service
 */
class FilesAPI {
  /**
   * Download a protected file with API key auth and return a blob + filename.
   * @param {string} url - Absolute or relative download URL
   * @param {string} [filenameHint] - Fallback filename if response headers omit it
   * @returns {Promise<{blob: Blob, filename: string}>}
   */
  async downloadFile(url, filenameHint = "project.xml") {
    try {
      const authSnapshot = getAuthTransportSnapshot();
      const response = await fetch(url, {
        method: "GET",
        headers: getRequestAuthHeaders("GET"),
        credentials: authSnapshot.credentialMethod === "legacy" ? "omit" : "include",
      });

      if (!response.ok) {
        let errorMessage = `Failed to download file (HTTP ${response.status})`;
        let errorPayload = null;
        try {
          errorPayload = await response.json();
          errorMessage = errorPayload?.error || errorPayload?.detail || errorMessage;
        } catch {
          // Ignore JSON parse failures for non-JSON error bodies.
        }

        const termsRequired = isTermsAcceptanceRequired(response.status, errorPayload);
        const credentialFailure =
          !termsRequired && isCredentialFailure(response.status, errorPayload);
        if (termsRequired) {
          notifyTermsAcceptanceRequired(authSnapshot);
        } else if (credentialFailure) {
          notifyAuthFailure(authSnapshot);
        }

        throw {
          type: termsRequired
            ? "terms_required"
            : credentialFailure
              ? "auth_error"
              : response.status === 403
                ? "authorization_error"
                : "files_error",
          status: response.status,
          message: errorMessage,
          details: { url, status: response.status },
        };
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition") || "";
      const filename = this.extractFilename(contentDisposition, filenameHint);

      return { blob, filename };
    } catch (error) {
      throw this.handleError(error, "Failed to download file");
    }
  }

  /**
   * Extract a filename from Content-Disposition, falling back safely.
   * @param {string} contentDisposition
   * @param {string} fallbackFilename
   * @returns {string}
   */
  extractFilename(contentDisposition, fallbackFilename) {
    if (!contentDisposition) return fallbackFilename;

    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }

    const bareMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (bareMatch?.[1]) {
      return bareMatch[1].trim();
    }

    return fallbackFilename;
  }

  /**
   * Upload a file to backend storage
   * @param {File|Blob} file - File instance from input
   * @param {Object} [options] - Optional metadata
   * @param {string} [options.projectName] - Project name for tracing
   * @param {string} [options.userId] - User id for tracing
   * @returns {Promise<Object>} Upload response
   */
  async uploadFile(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (options.projectName) formData.append("project_name", options.projectName);
      if (options.userId) formData.append("user_id", options.userId);

      const response = await apiClient.post(
        API_CONFIG.endpoints.files.upload,
        formData,
        options.signal ? { signal: options.signal } : undefined
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error, "Failed to upload file");
    }
  }

  /**
   * Handle files API errors
   * @param {Object} error - Error object
   * @param {string} defaultMessage - Default message
   * @returns {Object} Formatted error
   */
  handleError(error, defaultMessage) {
    if (error.type) return error;
    return {
      type: "files_error",
      message: error?.message || defaultMessage,
      details: error,
    };
  }
}

/**
 * Tier metadata - what model each tier resolves to right now on the backend.
 * The picker dropdown uses this list, so adding/removing a tier is a backend
 * config change (model_tiers.json) - no frontend redeploy needed.
 */
class ModelsAPI {
  async getTiers() {
    try {
      const response = await apiClient.get("/models/tiers");
      return response.data;
    } catch (error) {
      return this._buildErrorResponse(error, "Failed to load model tiers");
    }
  }

  _buildErrorResponse(error, defaultMessage) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || defaultMessage,
      details: error,
    };
  }
}

/**
 * Projects API Service
 *
 * Talks to the project identity resolver. The active-project endpoint is fed
 * by a sidecar that runs in-process with MS Project and reports the focused
 * project's identity. projectWatcher uses this to get a reliable mount-time
 * identity instead of the process-shared, focus-dependent Office.context.document.
 */
class ProjectsAPI {
  /**
   * Get the project MS Project currently has focused, as last reported by the
   * in-process sidecar. Never throws: returns null when the sidecar has posted
   * nothing yet (404) or on any transport error, so the caller can fall back
   * to Office.js.
   * @returns {Promise<Object|null>} { active_project, active_window_caption, sidecar_ts,
   *   age_seconds, tasks, tasks_match_window } or null. With a window token,
   *   tasks is populated only when the sidecar has a task snapshot for that same
   *   active window.
   */
  /**
   * @param {string|null} windowToken - Per-window token injected into the pane URL
   *   by the VSTO add-in (?window_token=<token>). When present the backend returns
   *   the project for that specific window rather than the globally-active one,
   *   resolving the simultaneous-mount race. Pass null to get global active.
   */
  async getActiveProject(windowToken = null) {
    try {
      const url = windowToken
        ? `/projects/active?window_token=${encodeURIComponent(windowToken)}`
        : "/projects/active";
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      // 404 = no snapshot yet; anything else = sidecar/transport down.
      // Either way the caller falls back to Office.js identity.
      return null;
    }
  }

  // GID-105: identity-only variant of getActiveProject. Same response shape but
  // the backend omits the task list, turning ~2MB on a 1000-task plan into a few
  // hundred bytes. projectWatcher's switch-detection read runs at mount and every
  // ~8s and only needs active_project identity + age_seconds — never tasks — so
  // it uses this instead of shipping the whole plan on every poll.
  async getActiveProjectIdentity(windowToken = null) {
    try {
      const params = { identity_only: "1" };
      if (windowToken) params.window_token = windowToken;
      const response = await apiClient.get("/projects/active", { params });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // GID-105: cheap fulfilment poll. Returns the still-pending request
  // ({ requested, request_id }) — a few bytes. Once the sidecar posts the fresh
  // snapshot the backend clears the request, so "no longer pending" means the
  // read landed. Deliberately NOT getActiveProject(): that returns the whole
  // task list (~2MB on a 1000-task plan) when all we need here is a yes/no.
  //
  // ALWAYS pass this pane's windowToken. Without it the backend answers for
  // whichever request is oldest across all the user's windows, so a second pane
  // sending at the same time would answer for us.
  async getSnapshotRequestStatus(windowToken = null) {
    try {
      const response = await apiClient.get("/projects/snapshot-request", {
        params: { window_token: windowToken || "" },
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // GID-105 read-on-send: ask the sidecar (via the backend) for a fresh COM read
  // of the active project before sending. Returns { request_id } or null on
  // failure — the caller polls getSnapshotRequestStatus() and falls back to
  // whatever snapshot exists if the read never lands.
  async requestSnapshot(windowToken = null) {
    try {
      const response = await apiClient.post("/projects/request-snapshot", {
        window_token: windowToken || "",
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // GID-143: take exclusive ownership of one MS Project window.
  //
  // A pane adopts the globally-active project's window_token at mount — it can
  // only have been opened in the focused window — but two panes mounting inside
  // one snapshot window would both read the same token. The backend grants it
  // to at most one, so the loser retries instead of binding to a window another
  // pane already owns.
  //
  // A refusal is a normal response body ({ granted: false, owner }), NOT an
  // error: the caller branches on it. Re-calling with the same instanceId is
  // how the claim is kept alive.
  async claimWindow(windowToken, instanceId) {
    try {
      const response = await apiClient.post("/projects/claim-window", {
        window_token: windowToken || "",
        instance_id: instanceId || "",
      });
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // Best-effort teardown. A pane torn down together with its MS Project window
  // never gets to send this at all, which is what the backend's claim TTL is
  // for — but the common case (the user closes just the pane) does reach here
  // and should not have to wait out that TTL.
  //
  // Sent with fetch + keepalive rather than through axios: this fires from
  // `pagehide`, and an ordinary in-flight XHR is cancelled when the page goes
  // away. keepalive is what lets the request outlive the document. Not
  // navigator.sendBeacon, which cannot carry the X-Api-Key header.
  async releaseWindow(windowToken, instanceId) {
    const body = JSON.stringify({
      window_token: windowToken || "",
      instance_id: instanceId || "",
    });

    if (typeof fetch === "function") {
      try {
        const authSnapshot = getAuthTransportSnapshot();
        const response = await fetch(`${API_CONFIG.baseURL}/projects/release-window`, {
          method: "POST",
          keepalive: true,
          // Same helper and credential mode as the other hand-rolled fetches
          // here. baseURL is window.location.origin, so the browser attaches
          // the session cookie by default -- and the cookie is the credential
          // the backend reads first. Sending only X-Api-Key therefore left a
          // cookie-authenticated POST with no X-CSRF-Token, which is refused
          // as CSRF_REQUIRED; and in legacy mode a stale cookie would win over
          // the key it was given, so that mode omits credentials outright.
          headers: {
            "Content-Type": "application/json",
            ...getRequestAuthHeaders("POST"),
          },
          credentials: authSnapshot.credentialMethod === "legacy" ? "omit" : "include",
          body,
        });
        return response.ok ? await response.json() : null;
      } catch (error) {
        return null;
      }
    }

    try {
      const response = await apiClient.post("/projects/release-window", JSON.parse(body));
      return response.data;
    } catch (error) {
      return null;
    }
  }

  async postPendingSync(projectGuid, commandId, commandHash) {
    const response = await apiClient.post("/projects/pending-sync", {
      project_guid: projectGuid,
      command_id: commandId,
      command_hash: commandHash || "",
    });
    return response.data;
  }

}

// Export API services
export const chatAPI = new ChatAPI();
export const healthAPI = new HealthAPI();
export const filesAPI = new FilesAPI();
export const modelsAPI = new ModelsAPI();
export const projectsAPI = new ProjectsAPI();

// Export default API client for custom requests
export default apiClient;
